import type { I3BlogPostCms } from './types';
import type { I3BlogPost } from '../data/i3BlogPosts';
import { resolveBlogFormat } from './blogFormat';

function parseIsoDate(s: string): number {
  const t = Date.parse(s);
  return Number.isNaN(t) ? 0 : t;
}

/** True when post should appear on public journal index and be reachable by slug. */
export function isBlogPostPubliclyVisible(post: I3BlogPostCms, now: Date = new Date()): boolean {
  if (post.status === 'draft' || post.status === 'archived') return false;
  if (post.scheduledPublishAt) {
    const t = parseIsoDate(post.scheduledPublishAt);
    if (t > now.getTime()) return false;
  }
  return true;
}

/** Archived: not in index; direct URL shows archive message (handled in page). */
export function isBlogPostArchived(post: I3BlogPostCms): boolean {
  return post.status === 'archived';
}

export function isBlogPostScheduledFuture(post: I3BlogPostCms, now: Date = new Date()): boolean {
  if (post.status !== 'published') return false;
  if (!post.scheduledPublishAt) return false;
  return parseIsoDate(post.scheduledPublishAt) > now.getTime();
}

export function cmsPostToLegacy(post: I3BlogPostCms): I3BlogPost {
  const legacy: I3BlogPost = {
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    author: post.author,
    publishedAt: post.publishedAt,
    format: post.format,
    topic: post.topic,
    featured: post.featured,
    readTimeMinutes: post.readTimeMinutes,
    heroImage: post.heroImage,
    quote: post.quote,
    meaning: post.meaning,
    paragraphs: post.paragraphs,
  };
  legacy.format = resolveBlogFormat(legacy);
  return legacy;
}

/**
 * Journal index: visible CMS posts first, then seed posts whose slugs are not on CMS.
 * CMS wins on slug collision. This keeps real CMS entries live while still showing the
 * full demo catalog when the JSON only has a few published articles.
 */
export function mergeBlogPostsForIndex(
  cmsPosts: I3BlogPostCms[] | undefined,
  fallback: I3BlogPost[],
  now: Date = new Date()
): I3BlogPost[] {
  const fromFallback = [...fallback];
  if (!cmsPosts?.length) {
    return fromFallback.sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
  }

  const visible = cmsPosts.filter((p) => isBlogPostPubliclyVisible(p, now));
  const mapped = visible.map(cmsPostToLegacy);
  const cmsSlugs = new Set(mapped.map((p) => p.slug));
  const extra = fromFallback.filter((p) => !cmsSlugs.has(p.slug));
  const combined = [...mapped, ...extra];

  return combined.sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );
}

export function findCmsBlogPost(
  cmsPosts: I3BlogPostCms[] | undefined,
  slug: string | undefined
): I3BlogPostCms | undefined {
  if (!slug || !cmsPosts?.length) return undefined;
  return cmsPosts.find((p) => p.slug === slug);
}
